import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { surveyAPI } from '../../utils/api';

const SurveyShow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [survey, setSurvey] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSurvey();
  }, [id]);

  const loadSurvey = async () => {
    try {
      setLoading(true);
      const response = await surveyAPI.getOne(id);
      setSurvey(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load survey');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Coaching': '#007bff',
      'Empowerment': '#28a745',
      'Care': '#dc3545',
      'Results': '#ffc107',
      'Communication': '#17a2b8',
      'Career': '#6610f2',
      'Vision': '#e83e8c',
      'TechnicalExpertise': '#fd7e14'
    };
    return colors[category] || '#6c757d';
  };

  const groupQuestionsByCategory = (questions) => {
    return questions.reduce((acc, question) => {
      if (!acc[question.category]) {
        acc[question.category] = [];
      }
      acc[question.category].push(question);
      return acc;
    }, {});
  };

  const styles = {
    container: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '2rem',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    header: {
      borderBottom: '2px solid #dee2e6',
      paddingBottom: '1rem',
      marginBottom: '2rem'
    },
    buttonGroup: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '1.5rem'
    },
    button: {
      padding: '0.75rem 1.5rem',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontWeight: '500',
      fontSize: '1rem'
    },
    backBtn: {
      backgroundColor: '#07638e',
      color: 'white'
    },
    editBtn: {
      background: '#07638e',
      color: 'white'
    },
    statsBtn: {
      backgroundColor: '#07638e',
      color: 'white'
    },
    infoSection: {
      backgroundColor: '#f8f9fa',
      padding: '1.5rem',
      borderRadius: '8px',
      marginBottom: '2rem'
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1.5rem',
      marginTop: '1rem'
    },
    infoItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem'
    },
    label: {
      fontSize: '0.875rem',
      color: '#6c757d',
      fontWeight: '600',
      textTransform: 'uppercase'
    },
    value: {
      fontSize: '1.125rem',
      color: '#212529',
      fontWeight: '500'
    },
    statusBadge: {
      display: 'inline-block',
      padding: '0.25rem 0.75rem',
      borderRadius: '12px',
      fontSize: '0.875rem',
      fontWeight: '600'
    },
    publishedBadge: {
      backgroundColor: '#d4edda',
      color: '#155724'
    },
    unpublishedBadge: {
      backgroundColor: '#f8d7da',
      color: '#721c24'
    },
    questionsSection: {
      marginTop: '2rem'
    },
    categoryCard: {
      backgroundColor: '#f8f9fa',
      padding: '1.5rem',
      marginBottom: '1.5rem',
      borderRadius: '8px',
      border: '2px solid #dee2e6'
    },
    categoryHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      marginBottom: '1rem'
    },
    categoryBadge: {
      padding: '0.5rem 1rem',
      borderRadius: '6px',
      color: 'white',
      fontWeight: '600',
      fontSize: '0.875rem'
    },
    questionList: {
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    questionItem: {
      backgroundColor: 'white',
      padding: '1rem',
      marginBottom: '0.75rem',
      borderRadius: '6px',
      border: '1px solid #dee2e6',
      display: 'flex',
      gap: '1rem',
      alignItems: 'flex-start'
    },
    questionNumber: {
      backgroundColor: '#007bff',
      color: 'white',
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      fontSize: '0.875rem',
      flexShrink: 0
    },
    questionText: {
      flex: 1,
      fontSize: '1rem',
      lineHeight: '1.5',
      color: '#212529'
    }
  };

  if (loading) return <div style={styles.container}>Loading survey...</div>;
  
  if (error) {
    return (
      <div style={styles.container}>
        <div style={{color: '#721c24', backgroundColor: '#f8d7da', padding: '1rem', borderRadius: '8px'}}>
          {error}
        </div>
      </div>
    );
  }

  if (!survey) return <div style={styles.container}>Survey not found</div>;

  const questionsByCategory = groupQuestionsByCategory(survey.questions || []);
  let questionCounter = 1;

  return (
    <div style={styles.container}>
      <div style={styles.buttonGroup}>
        <button 
          onClick={() => navigate('/admin/surveys')} 
          style={{...styles.button, ...styles.backBtn}}
        >
          Back to Surveys
        </button>
        <button 
          onClick={() => navigate(`/admin/surveys/${id}/edit`)} 
          style={{...styles.button, ...styles.editBtn}}
        >
          Edit Survey
        </button>
        <button 
          onClick={() => navigate(`/admin/surveys/${id}/stats`)} 
          style={{...styles.button, ...styles.statsBtn}}
        >
          View Stats
        </button>
      </div>

      <div style={styles.header}>
        <h2>{survey.title}</h2>
        <p style={{color: '#6c757d', margin: '0.5rem 0 0 0'}}>{survey.description}</p>
      </div>

      <div style={styles.infoSection}>
        <h3 style={{marginTop: 0, marginBottom: '1rem'}}>Survey Information</h3>
        <div style={styles.infoGrid}>
          <div style={styles.infoItem}>
            <div style={styles.label}>Status</div>
            <div>
              <span 
                style={{
                  ...styles.statusBadge,
                  ...(survey.published ? styles.publishedBadge : styles.unpublishedBadge)
                }}
              >
                {survey.published ? 'Published' : 'Unpublished'}
              </span>
            </div>
          </div>
          <div style={styles.infoItem}>
            <div style={styles.label}>Manager</div>
            <div style={styles.value}>
              {survey.manager ? (
                <>
                  <div>{survey.manager.full_name}</div>
                  <div style={{fontSize: '0.875rem', color: '#6c757d'}}>
                    {survey.manager.department}
                  </div>
                </>
              ) : (
                <span style={{color: '#6c757d'}}>No manager assigned</span>
              )}
            </div>
          </div>
          <div style={styles.infoItem}>
            <div style={styles.label}>Total Questions</div>
            <div style={styles.value}>{survey.questions_count || survey.questions?.length || 0}</div>
          </div>
          <div style={styles.infoItem}>
            <div style={styles.label}>Created</div>
            <div style={styles.value}>
              {new Date(survey.created_at).toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>

      <div style={styles.questionsSection}>
        <h3>Survey Questions</h3>
        {Object.keys(questionsByCategory).length === 0 ? (
          <div style={{textAlign: 'center', color: '#6c757d', padding: '2rem'}}>
            No questions added yet
          </div>
        ) : (
          Object.entries(questionsByCategory).map(([category, questions]) => (
            <div key={category} style={styles.categoryCard}>
              <div style={styles.categoryHeader}>
                <span 
                  style={{
                    ...styles.categoryBadge,
                    backgroundColor: getCategoryColor(category)
                  }}
                >
                  {category}
                </span>
                <span style={{color: '#6c757d', fontSize: '0.875rem'}}>
                  ({questions.length} question{questions.length !== 1 ? 's' : ''})
                </span>
              </div>
              <ul style={styles.questionList}>
                {questions.map((question) => (
                  <li key={question.id} style={styles.questionItem}>
                    <div style={styles.questionNumber}>{questionCounter++}</div>
                    <div style={styles.questionText}>{question.text}</div>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SurveyShow;
